import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SelectField from 'components/forms/selectField/SelectField';
import { useFormContext } from 'react-hook-form';
import { Nav } from 'react-bootstrap';
import useAnalyticsData from 'pages/analyticsAndHostQuality/analytics/useAnalyticsData';
import AnalyticsContext from 'pages/analyticsAndHostQuality/AnalyticsContext';
import { isCardDataApplicable } from 'pages/analyticsAndHostQuality/analytics/Analytics.utils';
import { AnalyticsKeyMetric } from 'pages/analyticsAndHostQuality/analytics/types/AnalyticsKeyMetrics.types';
import useAppUser from 'hooks/useAppUser';
import {
  ChartType,
  FinancialPerformanceMetric,
  PropertyPerformanceMetric,
  TeamPerformanceMetric,
} from '../Charts.types';
import { ChartsFilterFormWrapper } from './ChartsFilters.styles';

const ChartsFilters = () => {
  const { isOwner } = useAppUser();
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const { type, metric, offset, limit } = getValues();
  const [chartsDataOptions, setChartsDataOptions] = useState([]);
  const { allProperties, filter } = useContext(AnalyticsContext);
  const {
    isPropertyLoading,
    propertyError,
    isInitialPropertyLoading,
    propertyData,
  } = useAnalyticsData();

  const chartsTypeOptions = Object.values(ChartType)
    .filter((ct) => !(isOwner && ct === ChartType.TEAM_PERFORMANCE)) // Hide team performance chart for owner users
    .map((chartType: ChartType) => ({
      label: t(`pageAnalytics.chart.type.${chartType}`),
      value: chartType,
    }));

  const onNext = () => {
    setValue('offset', offset + limit);
  };

  const onPrevious = () => {
    setValue('offset', offset - limit);
  };

  const defaultRentalRevenueMetric = isOwner
    ? FinancialPerformanceMetric.OWNER_REVENUE
    : FinancialPerformanceMetric.RENTAL_REVENUE;

  useEffect(() => {
    if (type === ChartType.FINANCIAL_PERFORMANCE) {
      const financialMetrics = Object.values(FinancialPerformanceMetric)
        .filter((fpm) =>
          isOwner
            ? fpm === FinancialPerformanceMetric.OWNER_REVENUE
            : fpm !== FinancialPerformanceMetric.OWNER_REVENUE,
        )
        .map((data: FinancialPerformanceMetric) => ({
          label: t(`pageAnalytics.chart.metric.${data}`),
          value: data,
        }));

      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      setChartsDataOptions(financialMetrics);

      setValue(
        'metric',
        // @ts-expect-error TS7053 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        FinancialPerformanceMetric[metric]
          ? metric
          : defaultRentalRevenueMetric,
      );
    }

    if (type === ChartType.PROPERTY_PERFORMANCE) {
      const propertyMetrics = Object.values(PropertyPerformanceMetric).filter(
        (ppm) =>
          isOwner
            ? ppm !== PropertyPerformanceMetric.RENTAL_REVENUE &&
              ppm !== PropertyPerformanceMetric.AVERAGE_DAILY_RATE &&
              ppm !== PropertyPerformanceMetric.REV_PAR
            : ppm !== PropertyPerformanceMetric.OWNER_REVENUE,
      );

      setChartsDataOptions(
        // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        propertyMetrics
          .filter((ppm) =>
            isCardDataApplicable(filter, ppm as unknown as AnalyticsKeyMetric),
          )
          .map((data: PropertyPerformanceMetric) => ({
            label: t(`pageAnalytics.chart.metric.${data}`),
            value: data,
          })),
      );

      setValue(
        'metric',
        // @ts-expect-error TS7053 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        PropertyPerformanceMetric[metric] &&
          isCardDataApplicable(filter, metric)
          ? metric
          : defaultRentalRevenueMetric,
      );
      setValue('offset', 0);
    }

    if (type === ChartType.TEAM_PERFORMANCE) {
      setValue(
        'metric',
        // @ts-expect-error TS7053 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        TeamPerformanceMetric[metric]
          ? metric
          : TeamPerformanceMetric.LEADS_ASSIGNED,
      );

      setChartsDataOptions(
        // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        Object.values(TeamPerformanceMetric).map(
          (data: TeamPerformanceMetric) => ({
            label: t(`pageAnalytics.chart.metric.${data}`),
            value: data,
          }),
        ),
      );
    }
  }, [type, filter]);

  return (
    <ChartsFilterFormWrapper>
      <SelectField
        inline
        name="type"
        options={chartsTypeOptions}
        className="btn btn-chart-type"
        required
      />
      <SelectField
        inline
        name="metric"
        options={chartsDataOptions}
        className="btn btn-chart-metric"
        required
      />

      {type === ChartType.PROPERTY_PERFORMANCE &&
        // @ts-expect-error TS2532 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        propertyData?.reduce(
          // @ts-expect-error TS2769 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          (acc, metricValue) => [...acc, metricValue.propertyUid],
          [],
        ) > limit &&
        !propertyError &&
        !isInitialPropertyLoading && (
          <Nav>
            <ul className="pager">
              <li className="previous">
                <button
                  disabled={offset === 0 || isPropertyLoading}
                  type="button"
                  className="btn btn-link"
                  onClick={onPrevious}
                >
                  <span aria-hidden="true">&larr;</span>
                  {t(`pageAnalytics.previous`)}
                </button>
              </li>
              <li className="next">
                <button
                  disabled={
                    offset + limit > allProperties.length || isPropertyLoading
                  }
                  type="button"
                  className="btn btn-link"
                  onClick={onNext}
                >
                  {t(`pageAnalytics.next`)}
                  <span aria-hidden="true">&rarr;</span>
                </button>
              </li>
            </ul>
          </Nav>
        )}
    </ChartsFilterFormWrapper>
  );
};

export default ChartsFilters;
